import React, { useEffect, useRef, useState, useCallback } from 'react';
import { LOGO_VIEWBOX, LOGO_PATHS } from '../brand/MsMark';
import { lockScroll } from '../../utils/scrollLock';
import { appReady } from '../../utils/appReady';
import './Preloader.css';

/**
 * Uvodni loader: MS znak se "napaja" kao stampana ploca — konture se iscrtavaju
 * jedna za drugom (kao struja kroz trase), pa se delovi pale, pa energetski
 * impuls prelazi preko znaka i otkriva sajt.
 *
 * Pravila ponasanja:
 *  - prikazuje se pri svakom ucitavanju stranice (i pri refreshu). Komponenta
 *    se montira samo jednom po ucitavanju i stoji van Router-a, pa se NE
 *    ponavlja pri prelasku izmedju stranica kroz navigaciju;
 *  - ceka fontove i `window.load`, ali najmanje MIN_MS da se animacija odgleda
 *    i najvise MAX_MS da spor resurs nikad ne zakljuca stranicu;
 *  - uz `prefers-reduced-motion` nema iscrtavanja — samo kratak fade;
 *  - zakljucava skrol dok je vidljiv.
 */

const MIN_MS = 1500;   // koliko animacija traje do "impulsa"
const MAX_MS = 5000;   // sigurnosna kocnica ako se neki resurs zaglavi
const EXIT_MS = 900;   // trajanje izlazne animacije

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function Preloader({ onDone }) {
  // `window.__MSTECH_SKIP_INTRO__` je iskljucivo test prekidac (setupTests.js),
  // da svaki test ne bi cekao 1.9s vremenske ose uvoda. U pregledacu je uvek
  // undefined, pa se loader prikazuje pri svakom ucitavanju.
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.__MSTECH_SKIP_INTRO__ !== true;
  });
  const [leaving, setLeaving] = useState(false);
  const [pct, setPct] = useState(0);
  const reduced = useRef(prefersReducedMotion());
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const finish = useCallback(() => {
    setLeaving(true);
    window.setTimeout(() => {
      setVisible(false);
      doneRef.current?.();
    }, reduced.current ? 200 : EXIT_MS);
  }, []);

  // --- Zakljucaj skrol dok loader stoji ---
  useEffect(() => {
    if (!visible) return undefined;
    return lockScroll();
  }, [visible]);

  // --- Brojac procenata (rAF, ne setInterval — prati stvarno vreme) ---
  useEffect(() => {
    if (!visible || leaving) return undefined;
    if (reduced.current) { setPct(100); return undefined; }
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / MIN_MS, 1);
      // easeOutCubic — brzo krene pa se smiri pred kraj
      setPct(Math.round((1 - Math.pow(1 - p, 3)) * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, leaving]);

  // --- Kada zavrsiti: max(spremnost stranice, MIN_MS), ali nikad preko MAX_MS ---
  useEffect(() => {
    if (!visible) return undefined;

    let cancelled = false;
    const started = performance.now();

    const ready = Promise.all([
      document.fonts ? document.fonts.ready.catch(() => {}) : Promise.resolve(),
      document.readyState === 'complete'
        ? Promise.resolve()
        : new Promise((res) => window.addEventListener('load', res, { once: true })),
      // Bez ovoga se loader sklanjao pre nego što lazy stranica stigne, pa je
      // ostajao prazan tamni ekran — najvidljivije na telefonu.
      appReady(),
    ]);

    const minTimer = new Promise((res) =>
      window.setTimeout(res, reduced.current ? 150 : MIN_MS)
    );

    const hardStop = window.setTimeout(() => { if (!cancelled) finish(); }, MAX_MS);

    Promise.all([ready, minTimer]).then(() => {
      if (cancelled) return;
      window.clearTimeout(hardStop);
      // Ako je stranica bila spremna ranije, i dalje postujemo MIN_MS.
      const waited = performance.now() - started;
      window.setTimeout(finish, Math.max(0, (reduced.current ? 150 : MIN_MS) - waited));
    });

    return () => { cancelled = true; window.clearTimeout(hardStop); };
  }, [visible, finish]);

  if (!visible) return null;

  return (
    <div
      className={`preloader${leaving ? ' is-leaving' : ''}${reduced.current ? ' is-reduced' : ''}`}
      role="status"
      aria-live="polite"
      aria-label="Učitavanje sajta"
    >
      <div className="preloader__grid" aria-hidden="true" />

      <div className="preloader__stage">

        <svg
          className="preloader__mark"
          viewBox={LOGO_VIEWBOX}
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            {/* Impuls koji prelazi preko znaka kad se sve trase upale */}
            <linearGradient id="ms-surge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#fff" stopOpacity="0" />
              <stop offset="45%" stopColor="#fff" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <clipPath id="ms-clip">
              {LOGO_PATHS.map((d, i) => <path key={i} d={d} />)}
            </clipPath>
          </defs>

          {/* 1. Trase se iscrtavaju — stroke-dashoffset od 1 do 0 */}
          <g className="preloader__traces" fill="none" stroke="var(--accent)" strokeWidth="1.6">
            {LOGO_PATHS.map((d, i) => (
              <path key={i} d={d} pathLength="1" style={{ '--i': i }} />
            ))}
          </g>

          {/* 2. Delovi se pale kad njihova trasa zavrsi */}
          <g className="preloader__fills" fill="currentColor" fillRule="evenodd">
            {LOGO_PATHS.map((d, i) => (
              <path key={i} d={d} style={{ '--i': i }} />
            ))}
          </g>

          {/* 3. Impuls, ogranicen na oblik znaka */}
          <g clipPath="url(#ms-clip)">
            <rect className="preloader__surge" x="-120" y="-20" width="120" height="300" fill="url(#ms-surge)" />
          </g>
        </svg>

        <div className="preloader__meter" aria-hidden="true">
          <div className="preloader__bar" style={{ transform: `scaleX(${pct / 100})` }} />
        </div>

        <div className="preloader__readout" aria-hidden="true">
          <span className="preloader__label">MS&nbsp;TECHNOLOGY</span>
          <span className="preloader__pct">{String(pct).padStart(3, '0')}%</span>
        </div>
      </div>
    </div>
  );
}
